import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ICRUDOptions } from '@phx-erp/shared';
import { BaseListService } from '../../@core/services/base-list.service';
import {
    DELETE_PRODUCT_GROUP_INTEGRATION_LINK,
    GET_PRODUCT_GROUP_INTEGRATION_LINKS,
    SAVE_PRODUCT_GROUP_INTEGRATION_LINK,
} from '../../data/definitions/product-group-integration-link-definition';
import { QueryResult } from '../../data/query-result';
import { BaseDataService } from '../../data/services/base-data.service';
import { Condition, DeletionResponse, IntegrationConnection, Operator, SearchInput } from '../../generated/generated-types';

export interface ProductGroupIntegrationLink {
    id?: string | null;
    productGroupId?: string | null;
    integrationId?: string | null;
    integration?: IntegrationConnection | null;
    externalEntityId?: string | null;
    lockShopCategory?: boolean | null;
    lastSyncDate?: string | Date | null;
}

export interface ProductGroupIntegrationLinkInput {
    id?: string | null;
    productGroupId?: string | null;
    integrationId?: string | null;
    externalEntityId?: string | null;
    lockShopCategory?: boolean | null;
}

export interface ProductGroupIntegrationLinkSearchInput {
    take?: number;
    searchFilter?: {
        field: string;
        value: string;
        condition: Condition;
        operator: Operator;
    };
    productGroupId?: string;
    integrationId?: string;
}

interface GetProductGroupIntegrationLinksQuery {
    getProductGroupIntegrationLinks: {
        totalItems: number;
        items: ProductGroupIntegrationLink[];
    };
}

@Injectable({
    providedIn: 'root',
})
export class ProductGroupIntegrationLinkService extends BaseListService<ProductGroupIntegrationLink> {
    constructor(private baseDataService: BaseDataService) {
        super();
    }

    getItems(
        searchInput?: ProductGroupIntegrationLinkSearchInput,
        o?: ICRUDOptions,
    ): QueryResult<GetProductGroupIntegrationLinksQuery, { input: SearchInput }> {
        const fragment = GET_PRODUCT_GROUP_INTEGRATION_LINKS;
        if (o?.currentColumnConfig) {
            this.onlyKeepSelectedGraphQLNodesNew(
                fragment,
                o.currentColumnConfig.baseView,
                o.currentColumnConfig.columns,
            );
        }

        return this.baseDataService.query<GetProductGroupIntegrationLinksQuery, { input: SearchInput }>(
            fragment,
            { input: searchInput as SearchInput },
        );
    }

    async saveItem(input: ProductGroupIntegrationLinkInput): Promise<ProductGroupIntegrationLink> {
        const saved = await lastValueFrom(
            this.baseDataService.mutate<
                { saveProductGroupIntegrationLink: ProductGroupIntegrationLink },
                { input: ProductGroupIntegrationLinkInput }
            >(SAVE_PRODUCT_GROUP_INTEGRATION_LINK, { input }),
        );
        return saved.saveProductGroupIntegrationLink;
    }

    async deleteItem(id: string): Promise<DeletionResponse> {
        const result = await lastValueFrom(
            this.baseDataService.mutate<
                { deleteProductGroupIntegrationLink: DeletionResponse },
                { id: string }
            >(DELETE_PRODUCT_GROUP_INTEGRATION_LINK, { id }),
        );
        return result.deleteProductGroupIntegrationLink;
    }
}
